import { useMutation } from '@tanstack/react-query';

import {
  deleteV1DomainById,
  getV1DomainById,
  postV1Domain,
  putV1DomainById,
  putV1DomainByIdVerify,
  type DomainPatchBody,
  type DomainPostBody,
} from '../../../api/generated/pap';
import { getV1DomainsQueryKey } from '../../../api/generated/pap/@tanstack/react-query.gen';
import { queryClient } from '../../../api/queryClient';
import { getEtag } from '../../../utils/etag';

/**
 * Mutation hooks for the Domain Registration feature. See
 * `../IdentityProviders/identityProviders.mutations.ts` for the pattern.
 */

const invalidateDomains = () => queryClient.invalidateQueries({ queryKey: getV1DomainsQueryKey() });

export const useAddDomain = () =>
  useMutation({
    mutationFn: async (payload: DomainPostBody) => {
      const { data } = await postV1Domain({ body: payload, throwOnError: true });
      return data;
    },
    onSuccess: invalidateDomains,
  });

export const useUpdateDomain = () =>
  useMutation({
    mutationFn: async ({ id, payload }: { id: string; payload: DomainPatchBody }) => {
      const { data } = await putV1DomainById({ path: { id }, body: payload, throwOnError: true });
      return data;
    },
    onSuccess: invalidateDomains,
  });

export const useDeleteDomain = () =>
  useMutation({
    mutationFn: async (id: string) => {
      const { data } = await deleteV1DomainById({ path: { id }, throwOnError: true });
      return data;
    },
    onSuccess: invalidateDomains,
  });

export const useVerifyDomain = () =>
  useMutation({
    mutationFn: async ({ id }: { id: string }) => {
      // The verify endpoint requires the domain's current etag as If-Match, but
      // its URL differs from the domain resource URL, so the shared interceptor
      // can't supply it automatically. Refresh the domain first (capturing its
      // etag via the response interceptor) and pass it explicitly.
      await getV1DomainById({ path: { id }, throwOnError: true });
      const etag = getEtag(`/api/ciam/v1/domain/${id}`);
      const { data } = await putV1DomainByIdVerify({
        path: { id },
        ...(etag ? { headers: { 'If-Match': etag } } : {}),
        throwOnError: true,
      });
      return data;
    },
    onSuccess: invalidateDomains,
  });
